import { useShadcnNotification } from '@src/components/ui/notification';

import AiLogoIcon from '@shared/components/Icons/AiLogoIcon';
import { usePdfAiAnalysis } from '@shared/hooks/usePdfAiAnalysis';
import type { AiSummaryResponse } from '@shared/hooks/usePdfAiAnalysis';
import { downloadTextPdf } from '@shared/util/downloadTextPdf';

import type { FileContent } from './FilePreview';

import { ShadcnButton } from '@shadcn-features/core';
import { ShadcnSheet } from '@shadcn-features/core/wrapper/ShadcnSheet';
import { Download, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

type PdfAiAnalysisActionProps = {
    loadFile: () => FileContent | undefined | Promise<FileContent | undefined>;
};

export const PdfAiAnalysisAction = ({ loadFile }: PdfAiAnalysisActionProps) => {
    const { t } = useTranslation();
    const notification = useShadcnNotification();
    const { analyzePdf, isPending } = usePdfAiAnalysis();
    const [result, setResult] = useState<AiSummaryResponse>();
    const [open, setOpen] = useState(false);

    const handleAnalysis = async () => {
        try {
            const file = await loadFile();

            if (!file?.content) {
                notification.warning({
                    message: t('translation:fileNotFound', {
                        defaultValue: 'Файл не найден',
                    }),
                    description: t('translation:fileNotFound', {
                        defaultValue: 'Файл не найден',
                    }),
                });
                return;
            }
            const nextResult = await analyzePdf(file);
            if (!nextResult) return;

            setResult(nextResult);
            setOpen(true);
        } catch {
            // HTTP errors are displayed by the shared query handler.
        }
    };

    const handleDownload = async () =>
        result?.summary &&
        downloadTextPdf(
            result.summary,
            t('translation:aiAnalysisPdfFileName', {
                defaultValue: 'AI-анализ.pdf',
            }),
        );

    return (
        <>
            <ShadcnButton
                className='w-auto! px-2.5!'
                icon={<Sparkles size={16} />}
                loading={isPending}
                type='button'
                variant='aiAnalysis'
                onClick={handleAnalysis}
            >
                <span className='hidden lg:block'>
                    {isPending
                        ? t('translation:generation', {
                              defaultValue: 'Генерация',
                          })
                        : t('translation:aiTextAnalysis', {
                              defaultValue: 'AI-анализ текста',
                          })}
                </span>
            </ShadcnButton>

            <ShadcnSheet
                className='z-[9999999999]'
                open={open}
                placement='right'
                width={560}
                footer={
                    <p className='font-body w-full text-center text-sm leading-[16.5px] font-medium text-(--color-neutral-7)'>
                        {t('translation:aiAnalysisDisclaimer', {
                            defaultValue:
                                'Сгенерировано AI · перепроверяйте важную информацию',
                        })}
                    </p>
                }
                styles={{
                    body: {
                        overflow: 'hidden',
                        padding: 0,
                    },
                    footer: {
                        borderTop: '1px solid var(--color-border)',
                        padding: 16,
                    },
                    header: {
                        background: 'var(--color-neutral-3)',
                        padding: '14px 24px',
                    },
                }}
                title={t('translation:aiAnalysis', {
                    defaultValue: 'AI-анализ',
                })}
                onClose={() => {
                    setOpen(false);
                }}
            >
                <div className='relative flex h-full min-h-0 flex-col overflow-hidden p-6 pt-0'>
                    <div className='relative z-10 flex min-h-0 flex-1 flex-col overflow-y-auto'>
                        <div className='flex items-center justify-between gap-4 border-b border-(--color-border) pb-4'>
                            <div className='flex min-w-0 items-center gap-2'>
                                <span className='flex size-6 shrink-0 items-center justify-center rounded-[5px] bg-[linear-gradient(224deg,#16f099_7%,#089d61_93%)]'>
                                    <AiLogoIcon className='h-[9px] w-4 text-white' />
                                </span>
                                <span className='font-body truncate text-[13px] leading-[19.5px] font-semibold tracking-[0.13px] text-(--color-select-text)'>
                                    {t('translation:aiAnalysisReady', {
                                        defaultValue: 'AI-анализ готов',
                                    })}
                                </span>
                            </div>

                            <ShadcnButton
                                className='font-body! h-7 shrink-0 gap-1 rounded-md! border-(--color-primary-3)! bg-(--color-primary-1)! px-2! py-1.5! text-xs! text-(--color-primary-6)! hover:bg-(--color-primary-1)!'
                                icon={<Download size={12} />}
                                size='mini'
                                type='button'
                                variant='outlineBrandGreen'
                                onClick={handleDownload}
                            >
                                {t('translation:downloadPdf', {
                                    defaultValue: 'Скачать PDF',
                                })}
                            </ShadcnButton>
                        </div>

                        <div className='pt-4'>
                            <h3 className='font-body mb-2 text-xs leading-[16.5px] font-semibold tracking-[0.77px] text-(--color-primary-6) uppercase'>
                                {t('translation:briefSummary', {
                                    defaultValue: 'Краткое содержание',
                                })}
                            </h3>
                            <p className='font-body [text-align:justify] text-sm leading-5 font-medium whitespace-pre-wrap text-(--color-select-text)'>
                                {result?.summary}
                            </p>
                        </div>
                    </div>

                    <AiLogoIcon
                        aria-hidden
                        className='pointer-events-none absolute -right-24 bottom-4 w-[360px] text-(--color-neutral-3)'
                    />
                </div>
            </ShadcnSheet>
        </>
    );
};
